/**
 * IncidentsListPage — cross-project list of all safety incidents with
 * filters by status/type/severity. Status filter defaults to "active"
 * (open + investigating) so the page opens on "what needs my
 * attention" rather than the full history.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ShieldAlert, HeartPulse, Wrench, Plus, Filter, Clock, CheckCircle2 } from "lucide-react";

const TYPE_META = {
  stop_work:        { label: "Stop-Work",       icon: ShieldAlert,   color: "red" },
  injury:           { label: "Injury",          icon: HeartPulse,    color: "red" },
  near_miss:        { label: "Near Miss/Good Catch", icon: AlertTriangle, color: "amber" },
  property_damage:  { label: "Property Damage", icon: Wrench,        color: "amber" },
};
const SEV_META = {
  critical: { label: "CRITICAL", cls: "bg-red-600 text-white" },
  high:     { label: "HIGH",     cls: "bg-red-800 text-red-100" },
  medium:   { label: "MED",      cls: "bg-amber-700 text-amber-100" },
  low:      { label: "LOW",      cls: "bg-slate-700 text-slate-200" },
};
const STATUS_META = {
  open:          { label: "Open",          cls: "bg-red-900/60 text-red-200 border-red-800" },
  investigating: { label: "Investigating", cls: "bg-amber-900/60 text-amber-200 border-amber-800" },
  resolved:      { label: "Resolved",      cls: "bg-emerald-900/60 text-emerald-200 border-emerald-800" },
  closed:        { label: "Closed",        cls: "bg-slate-800 text-slate-300 border-slate-700" },
};

function relTime(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const d = Math.floor(diff / (24 * 3600 * 1000));
    if (d >= 1) return `${d}d ago`;
    const h = Math.floor(diff / (3600 * 1000));
    if (h >= 1) return `${h}h ago`;
    return "just now";
  } catch { return ""; }
}

export default function IncidentsListPage() {
  const location = useLocation();
  const projectFilter = new URLSearchParams(location.search).get("project") || "";
  const [status, setStatus] = useState("active"); // pseudo — expands to open,investigating server-side
  const [type, setType] = useState("");
  const [severity, setSeverity] = useState("");
  const [items, setItems] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams();
    if (status === "active") q.set("status", "open,investigating");
    else if (status) q.set("status", status);
    if (type) q.set("type", type);
    if (severity) q.set("severity", severity);
    if (projectFilter) q.set("project_id", projectFilter);
    setItems(null);
    // Guard against out-of-order responses: a slow request for
    // filter A can resolve AFTER a fast request for filter B and
    // paint stale results. `stale` flips true on cleanup so a
    // late resolver knows to drop its payload.
    let stale = false;
    api.get(`/incidents?${q.toString()}`)
      .then((r) => { if (!stale) setItems(r.data.items || []); })
      .catch(() => { if (!stale) setItems([]); });
    return () => { stale = true; };
  }, [status, type, severity, projectFilter]);

  const grouped = useMemo(() => {
    if (!items) return {};
    return items.reduce((acc, it) => {
      (acc[it.status] = acc[it.status] || []).push(it);
      return acc;
    }, {});
  }, [items]);

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-slate-400 font-display">Safety</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="incidents-heading">
            Incidents
          </h1>
          {projectFilter && (
            <div className="text-xs text-blue-300 mt-1 flex items-center gap-1" data-testid="incidents-project-filter-chip">
              Filtered to a single project ·
              <Link to="/incidents" className="underline underline-offset-2 hover:text-blue-100">clear filter</Link>
            </div>
          )}
        </div>
        <Button asChild className="bg-red-700 hover:bg-red-600 font-display tracking-wider" data-testid="incidents-new-btn">
          <Link to="/incidents/new"><Plus className="w-4 h-4 mr-1.5" />REPORT INCIDENT</Link>
        </Button>
      </div>

      {/* Filter row */}
      <Card className="bg-slate-900 border-slate-800 mb-4">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold">
              <Filter className="w-3.5 h-3.5" /> Filter
            </div>
            <FilterGroup label="Status" value={status} onChange={setStatus} testidPrefix="incidents-filter-status"
              options={[["active","Active"],["open","Open"],["investigating","Investigating"],["resolved","Resolved"],["closed","Closed"],["",  "All"]]} />
            <FilterGroup label="Type" value={type} onChange={setType} testidPrefix="incidents-filter-type"
              options={[["", "All"],["stop_work","Stop-Work"],["injury","Injury"],["near_miss","Near Miss/Good Catch"],["property_damage","Property"]]} />
            <FilterGroup label="Severity" value={severity} onChange={setSeverity} testidPrefix="incidents-filter-sev"
              options={[["", "All"],["critical","Critical"],["high","High"],["medium","Medium"],["low","Low"]]} />
          </div>
        </CardContent>
      </Card>

      {items === null ? (
        <div className="text-slate-500 text-sm py-10 text-center" data-testid="incidents-loading">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800" data-testid="incidents-empty">
          <CardContent className="p-8 text-center space-y-2">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto" />
            <div className="font-display font-bold tracking-tight">No incidents match this filter</div>
            <div className="text-xs text-slate-400">
              {status === "active"
                ? "Nothing open or under investigation — the crew is running clean."
                : "Try widening the status filter or reporting a new incident."}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="incidents-list">
          {Object.entries(grouped).map(([s, list]) => (
            <div key={s}>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-display font-bold mb-1.5 pl-1">
                {STATUS_META[s]?.label || s} · {list.length}
              </div>
              <div className="space-y-2">
                {list.map((it) => <IncidentRow key={it.id} it={it} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}

function FilterGroup({ label, value, onChange, options, testidPrefix }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">{label}</span>
      {options.map(([v, l]) => (
        <button
          key={v || "all"}
          type="button"
          onClick={() => onChange(v)}
          className={`h-7 px-2.5 rounded-full text-[11px] font-display font-bold uppercase tracking-wider transition-colors border ${
            value === v
              ? "bg-slate-700 border-slate-500 text-white"
              : "border-slate-800 bg-slate-950 text-slate-400 hover:bg-slate-800"
          }`}
          data-testid={`${testidPrefix}-${v || "all"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function IncidentRow({ it }) {
  const meta = TYPE_META[it.type] || TYPE_META.near_miss;
  const Icon = meta.icon;
  const sev = SEV_META[it.severity] || SEV_META.medium;
  const st = STATUS_META[it.status] || STATUS_META.open;
  const openCAs = (it.corrective_actions || []).filter((c) => !c.completed_at).length;
  const doneCAs = (it.corrective_actions || []).filter((c) => c.completed_at).length;
  return (
    <Link
      to={`/incidents/${it.id}`}
      className="flex items-start gap-3 p-3 rounded-md border border-slate-800 bg-slate-900 hover:bg-slate-800 transition-colors"
      data-testid={`incident-row-${it.id}`}
    >
      <div className={`p-2 rounded-md border shrink-0 ${meta.color === "red" ? "border-red-900 bg-red-950/40 text-red-300" : "border-amber-900 bg-amber-950/40 text-amber-300"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="font-display font-bold text-sm">{meta.label}</span>
          <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${sev.cls}`}>{sev.label}</span>
          <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${st.cls}`}>{st.label}</span>
          {it.auto_created && (
            <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-slate-700 bg-slate-950 text-slate-400">
              Auto
            </span>
          )}
        </div>
        <div className="text-sm text-slate-200 truncate">{it.description}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
          {it.site_number && `${it.site_number} · `}
          {it.customer && `${it.customer} · `}
          {it.reported_by_name && `by ${it.reported_by_name} · `}
          {relTime(it.reported_at)}
        </div>
        {(openCAs > 0 || doneCAs > 0) && (
          <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Corrective actions: {doneCAs}/{doneCAs + openCAs} complete
          </div>
        )}
      </div>
    </Link>
  );
}
